import { EvidenceUploadMode } from 'api';
import { useTranslation } from 'i18n';
import { logger } from 'monitor';
import { useState } from 'react';
import {
  Box,
  IconButton,
  MediaDialog,
  type PhotoEvidenceItem,
  PhotoEvidenceList,
  Stack,
  Tooltip,
  Typography,
  useBreakpoint,
} from 'ui';
import { Delete, Sync } from 'ui/icons';
import { useNetworkStatus } from '../../../network-status';
import { useNotification } from '../../../notifications/hooks/useNotification/useNotification';
import { useOfflineEvidence } from '../../hooks/useOfflineEvidence/useOfflineEvidence';

export const OfflineEvidenceGallery = ({
  parentId,
  mode,
  onSyncComplete,
}: {
  parentId: string;
  mode: EvidenceUploadMode;
  onSyncComplete?: () => void;
}) => {
  const { t } = useTranslation('features');
  const { t: tCommon } = useTranslation('common');
  const { isMobile } = useBreakpoint();
  const { isOnline } = useNetworkStatus();
  const { open } = useNotification();
  const { offlinePhotos, clearOfflineEvidence, syncOfflineEvidence, isSyncing, isClearing } = useOfflineEvidence({
    mode,
    onSyncComplete,
    parentId,
  });

  const offlineCarouselMedia = offlinePhotos.map((photo) => photo.url);
  const [selectedOfflineImage, setSelectedOfflineImage] = useState<string>('');

  const handleSync = async () => {
    logger.info('Offline evidence gallery sync clicked', {
      mode,
      offlinePhotosCount: offlinePhotos.length,
      parentId,
    });
    const result = await syncOfflineEvidence();
    logger.info('Offline evidence gallery sync completed', {
      mode,
      parentId,
      resultMessage: result.message,
      resultSuccess: result.success,
      syncedCount: result.results.length,
    });
    open({
      message: result.message,
      type: result.success ? 'success' : 'error',
    });
  };

  const handleClear = async () => {
    logger.info('Offline evidence gallery clear clicked', {
      mode,
      offlinePhotosCount: offlinePhotos.length,
      parentId,
    });
    try {
      await clearOfflineEvidence();
      logger.info('Offline evidence gallery clear completed', {
        mode,
        parentId,
      });
      open({
        message: t('evidence.offlineGallery.notifications.clearSuccess'),
        type: 'success',
      });
    } catch (error) {
      logger.error('Offline evidence gallery clear failed', error, {
        mode,
        parentId,
      });
      open({
        message: t('evidence.offlineGallery.notifications.clearError'),
        type: 'error',
      });
    }
  };

  const getTooltipMessage = () => {
    if (isSyncing) {
      return t('evidence.offlineGallery.tooltips.syncInProgress');
    }
    if (!isOnline) {
      return t('evidence.offlineGallery.tooltips.cannotSyncWhileOffline');
    }
    return t('evidence.offlineGallery.tooltips.sync');
  };

  return (
    <>
      <Stack
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="body1" fontWeight="bold">
          {t('evidence.offlineGallery.title', { values: { evidenceLower: tCommon('domains.evidenceLower') } })}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
          }}
        >
          <Tooltip title={t('evidence.offlineGallery.tooltips.clear')}>
            <div>
              <IconButton onClick={handleClear} disabled={isSyncing || isClearing}>
                <Delete />
              </IconButton>
            </div>
          </Tooltip>
          <Tooltip title={getTooltipMessage()}>
            <div>
              <IconButton disabled={!isOnline || isSyncing || isClearing} onClick={handleSync}>
                <Sync
                  sx={{
                    '@keyframes spin': {
                      '0%': {
                        transform: 'rotate(0deg)',
                      },
                      '100%': {
                        transform: 'rotate(-360deg)',
                      },
                    },
                    animation: isSyncing ? 'spin 2s linear infinite' : 'none',
                  }}
                />
              </IconButton>
            </div>
          </Tooltip>
        </Box>
      </Stack>
      <PhotoEvidenceList
        openOnClick
        slotProps={{
          imageList: {
            cols: isMobile ? 3 : 5,
          },
        }}
        evidencePhotos={offlinePhotos.map((photo) => ({
          src: photo.url,
        }))}
        handleClick={(photo: PhotoEvidenceItem) => {
          logger.info('Offline evidence gallery photo opened', {
            mode,
            offlinePhotosCount: offlinePhotos.length,
            parentId,
          });
          setSelectedOfflineImage(photo.src);
        }}
      />
      <MediaDialog
        open={Boolean(selectedOfflineImage)}
        selectedMedium={selectedOfflineImage}
        onClose={() => {
          logger.info('Offline evidence gallery photo dialog closed', {
            mode,
            parentId,
          });
          setSelectedOfflineImage('');
        }}
        media={offlineCarouselMedia}
        handleNavigate={(medium) => {
          logger.info('Offline evidence gallery photo dialog navigated', {
            mode,
            parentId,
          });
          setSelectedOfflineImage(medium);
        }}
        imageCount={offlineCarouselMedia.length}
      />
    </>
  );
};
